/**
 * Copyright (C) 2018-present, Botobag Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import Decimal from 'decimal.js';

// Money is composed of an amount and a currency.
// https://frontstuff.io/how-to-handle-monetary-values-in-javascript#engineering-to-the-rescue
export interface Money {
  amount: Decimal;
  // FIXME: Use enum for currency.
  currency: string;
}

export type MoneyAmountData = string;
export const ZERO = new Decimal(0);

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
// 'style' is fixed to 'currency' when using MoneyFormatter.
export type MoneyFormatterOptions = {
  locale?: string;
} & Omit<Intl.NumberFormatOptions, 'style' | 'currency'>;

export function newMoneyFormatter(currency: string, options: MoneyFormatterOptions = {}) {
  options = Object.assign<Partial<Intl.NumberFormatOptions>, MoneyFormatterOptions>({
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
    useGrouping: true,
    // TWD doesn't use subunit anymore.
    minimumFractionDigits: (currency === 'TWD') ? 0 : undefined,
  }, options);

  return new Intl.NumberFormat(options.locale, options);
}

export function formatMoney(money: Money, options: MoneyFormatterOptions = {}) {
  return newMoneyFormatter(money.currency, options).format(money.amount.toNumber());
}
